import { useState, useEffect, useCallback, useRef } from 'react';
import { api, GrSubscriptionResponse } from '@/lib/api';

interface UseAdminSubscriptionsOptions {
  userId?: string;
  isActive?: boolean;
  tier?: string;
}

interface UseAdminSubscriptionsReturn {
  subscriptions: GrSubscriptionResponse[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useAdminSubscriptions(
  options: UseAdminSubscriptionsOptions = {},
): UseAdminSubscriptionsReturn {
  const [subscriptions, setSubscriptions] = useState<GrSubscriptionResponse[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchIdRef = useRef(0);

  const fetchSubscriptions = useCallback(async () => {
    const id = ++fetchIdRef.current;
    try {
      setLoading(true);
      const result = await api.listGameRewardsSubscriptions({
        userId: options.userId || undefined,
        isActive: options.isActive,
        tier: options.tier || undefined,
      });
      if (fetchIdRef.current !== id) return;
      setSubscriptions(result.subscriptions);
      setTotal(result.total);
    } catch (err) {
      if (fetchIdRef.current !== id) return;
      setError(err instanceof Error ? err.message : 'Failed to load subscriptions');
    } finally {
      if (fetchIdRef.current === id) {
        setLoading(false);
      }
    }
  }, [options.userId, options.isActive, options.tier]);

  const refresh = useCallback(() => {
    setError(null);
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  useEffect(() => {
    refresh();
    return () => {
      fetchIdRef.current++;
    };
  }, [refresh]);

  return { subscriptions, total, loading, error, refresh };
}
